import React, { FC, ReactElement, useContext, useEffect, useState } from 'react'
import { useFormContext, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { ScopeAssignment as ScopeAssignmentLanguage } from '../../../../i18n'
import { LOADING_STRING } from '../../../NonReact/constants/Common'
// Language
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { FilterDefaults } from '../../Common/Functions/FilterDefaults'
import {
    ALL_VALUE,
    getCMEsAsSelectOptions,
    getPartitionsForTelServer,
    getTelServersAsSelectOptions
} from '../../Common/API/ItemsGetters'
import { ComboBox } from '../../Common/Components/ComboBox/ComboBox'
import FormRow from '../../Common/Components/FormRow'
import { SelectOptions } from '../../Common/Components/Selects'
import { ReactState } from '../../Common/Contexts'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { handleUnknownForAvailables, removeUnknownOption } from '../../Common/Functions/CommonFunction'
import {
    CME_SCOPE_NAME,
    FILTER_RECURSIVELY_SCOPE_NAME,
    PARTITION_SCOPE_NAME,
    TELEPHONY_SERVER_SCOPE_NAME
} from '../Constants/FieldNames'
import { FilterValues } from '../Types/Types'
import { AKIXI_CME_NAME, DEFAULT_CME_ID, SERVER_UNKNOWN_VALUE } from '../../Common/Constants/ServerValues'
import { OutlinedSelect } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelect'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'

type Props = {
    filters?: FilterValues
    viewOnly: boolean | undefined
}

type DefaultAvailables = [SelectOptions<string>, SelectOptions<number>, SelectOptions<number>]

/**
 * Get the available entities defaults.
 *
 * @param scope - Form permissions from the server.
 *
 * @returns - An array of defaults.
 */
const getAvailableEntitiesDefaults = (filters?: FilterValues): DefaultAvailables => [
    [
        {
            label: filters?.cmeID ?? filters?.cmeID ?? AKIXI_CME_NAME,
            value: filters?.cmeID ?? DEFAULT_CME_ID
        }
    ],
    [
        {
            label: LOADING_STRING,
            value: !IsVarEmpty(filters?.telSysID) ? parseInt(`${filters?.telSysID}`, 10) : ALL_VALUE
        }
    ],
    [
        {
            label: LOADING_STRING,
            value: filters?.partitionID ?? ALL_VALUE
        }
    ]
]

const ScopeAssignment: FC<Props> = ({ filters, viewOnly }: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(ScopeAssignmentLanguage.NAME)

    const { displayError }: MessageContextType = useContext(MessageContext)

    const [cmeDefaults, telSysDefaults, partitionDefaults]: DefaultAvailables = getAvailableEntitiesDefaults(filters)

    const [availableCMEs, setAvailableCMEs]: ReactState<SelectOptions<string>> =
        useState<SelectOptions<string>>(cmeDefaults)

    const [availableTelServers, setAvailableTelServers]: ReactState<SelectOptions<number>> =
        useState<SelectOptions<number>>(telSysDefaults)

    const [availablePartitions, setAvailablePartitions]: ReactState<SelectOptions<number>> =
        useState<SelectOptions<number>>(partitionDefaults)

    const [isLoadingTelServers, setIsLoadingTelServers]: ReactState<boolean> = useState<boolean>(false)
    const [isLoadingPartitions, setIsLoadingPartitions]: ReactState<boolean> = useState<boolean>(false)

    const isLoading: boolean = isLoadingTelServers || isLoadingPartitions

    const filterDefaults: FilterDefaults = FilterDefaults.getInstance(language)

    // Contexts
    const { watch, setValue }: UseFormMethods = useFormContext()

    const cmeValue: string = watch(CME_SCOPE_NAME)
    const telServerValue: number = watch(TELEPHONY_SERVER_SCOPE_NAME)
    const partitionValue: number = watch(PARTITION_SCOPE_NAME)
    const filterRecursively: boolean = watch(FILTER_RECURSIVELY_SCOPE_NAME)

    useEffect((): void => {
        getCMEsAsSelectOptions()
            .then((cmes: SelectOptions<string>): void => {
                handleUnknownForAvailables(cmes, setAvailableCMEs, cmeValue)
            })
            .catch(displayError)
    }, [])

    useEffect((): void => {
        if (!IsStringEmpty(cmeValue) && cmeValue !== SERVER_UNKNOWN_VALUE) {
            setIsLoadingTelServers(true)
            getTelServersAsSelectOptions(cmeValue, filterRecursively)
                .then((telServers: SelectOptions<number>): void => {
                    setIsLoadingTelServers(false)
                    handleUnknownForAvailables(telServers, setAvailableTelServers, telServerValue)
                })
                .catch((err: Error) => {
                    setIsLoadingTelServers(false)
                    displayError(err)
                })
        } else {
            handleUnknownForAvailables([], setAvailableTelServers, telServerValue)
            handleUnknownForAvailables([], setAvailablePartitions, partitionValue)
        }

        removeUnknownOption(setAvailableCMEs, cmeValue)
    }, [cmeValue, filterRecursively])

    useEffect((): void => {
        if (!IsVarEmpty(telServerValue) && telServerValue !== ALL_VALUE) {
            setIsLoadingPartitions(true)
            getPartitionsForTelServer(telServerValue)
                .then((partitions: SelectOptions<number>): void => {
                    setIsLoadingPartitions(false)
                    handleUnknownForAvailables(partitions, setAvailablePartitions, partitionValue)
                })
                .catch((err: Error) => {
                    setIsLoadingPartitions(false)
                    displayError(err)
                })
        } else {
            handleUnknownForAvailables([], setAvailablePartitions, partitionValue)
        }

        removeUnknownOption(setAvailablePartitions, telServerValue)
    }, [telServerValue])

    return (
        <div className='codes-details-tab tab-area'>
            <FormRow>
                <OutlinedSelect
                    name={CME_SCOPE_NAME}
                    label={language.get(ScopeAssignmentLanguage.SCOPEASSIGNMENT_CME)}
                    options={availableCMEs}
                    noChange={isLoading}
                    disabled={viewOnly}
                    handleChange={(): void => {
                        setValue(PARTITION_SCOPE_NAME, ALL_VALUE)
                    }}
                />
            </FormRow>
            <FormRow>
                <ComboBox
                    disabled={viewOnly}
                    name={TELEPHONY_SERVER_SCOPE_NAME}
                    label={language.get(ScopeAssignmentLanguage.SCOPEASSIGNMENT_TelServer)}
                    variant='outlined'
                    defaultOption={filterDefaults.getAllTelServersOption()}
                    options={availableTelServers}
                    loading={isLoadingTelServers}
                    noChange={isLoading}
                    onChange={(): void => {
                        setValue(PARTITION_SCOPE_NAME, ALL_VALUE)
                    }}
                />
            </FormRow>
            <FormRow>
                <ComboBox
                    disabled={viewOnly}
                    name={PARTITION_SCOPE_NAME}
                    variant='outlined'
                    label={language.get(ScopeAssignmentLanguage.SCOPEASSIGNMENT_Partition)}
                    defaultOption={filterDefaults.getAnyPartitionOption()}
                    options={availablePartitions}
                    loading={isLoadingPartitions}
                    noChange={isLoading}
                    multiple={false}
                />
            </FormRow>
            <FormRow>
                <RouterCheckbox
                    disabled={viewOnly}
                    labelLeft={language.get(ScopeAssignmentLanguage.SCOPEASSIGNMENT_FilterRecursively)}
                    name={FILTER_RECURSIVELY_SCOPE_NAME}
                />
            </FormRow>
        </div>
    )
}

export default ScopeAssignment
